import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { LayoutDashboard, ListTodo, KanbanSquare, Plus, CheckCircle2, Clock } from 'lucide-react';

// API base URL: set REACT_APP_API_URL in .env for live backend (e.g. https://your-api.railway.app)
// If unset or REACT_APP_USE_MOCK=true, app runs in demo mode with in-memory data (no backend needed).
const API_BASE = process.env.REACT_APP_API_URL || '';
const USE_MOCK = process.env.REACT_APP_USE_MOCK === 'true' || !API_BASE;

const MOCK_TASKS = [
  { id: '1', title: 'Draft social media copy', project: 'Client A Campaign', category: 'my_day', meta: 'Assigned: You', status: 'In Progress' },
  { id: '2', title: 'Review new logo concepts', project: 'Client B Rebrand', category: 'my_day', meta: 'Assigned: You', status: 'In Review' },
  { id: '3', title: 'Prepare for weekly team sync', project: 'Internal', category: 'my_day', meta: 'Due: 3:00 PM', status: 'In Progress' },
  { id: '4', title: 'Finalise website mock-ups', project: 'Client B Website', category: 'upcoming', meta: 'Due: Tomorrow', status: 'Backlog' },
  { id: '5', title: 'Launch email sequence', project: 'Client A Campaign', category: 'upcoming', meta: 'Due: Friday', status: 'Backlog' }
];

const MOCK_STATS = [
  { name: 'Sarah', hours: 80, color: '#8884d8' },
  { name: 'David', hours: 60, color: '#82ca9d' },
  { name: 'Maria', hours: 90, color: '#ffc658' },
  { name: 'Tom', hours: 40, color: '#ff8042' }
];

function App() {
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState([]);
  const [view, setView] = useState('my_day'); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  // Fetch Data: use API if configured, else (or on failure) use mock data for standalone demo
  useEffect(() => {
    if (USE_MOCK) {
      setTasks([...MOCK_TASKS]);
      setStats([...MOCK_STATS]);
      return;
    }
    Promise.all([
      fetch(`${API_BASE}/tasks`).then(res => res.ok ? res.json() : Promise.reject(res)),
      fetch(`${API_BASE}/dashboard-stats`).then(res => res.ok ? res.json() : Promise.reject(res))
    ])
      .then(([tasksData, statsData]) => {
        setTasks(tasksData);
        setStats(statsData);
      })
      .catch(() => {
        setTasks([...MOCK_TASKS]);
        setStats([...MOCK_STATS]);
      });
  }, []);

  // Handle Drag & Drop
  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId;
    const newTasks = tasks.map(t => t.id === draggableId ? { ...t, status: newStatus } : t);
    setTasks(newTasks);

    if (!USE_MOCK) {
      fetch(`${API_BASE}/tasks/${draggableId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      }).catch(() => setTasks(tasks)); // revert on failure
    }
  };

  // Handle Create Task
  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle) return;

    if (USE_MOCK) {
      const newId = String(Date.now());
      const newTask = {
        id: newId,
        title: newTaskTitle,
        project: 'New Project',
        category: 'my_day',
        meta: 'Just Added',
        status: 'Backlog'
      };
      setTasks([...tasks, newTask]);
      setNewTaskTitle('');
      setIsModalOpen(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTaskTitle, project: 'New Project', category: 'my_day' })
      });
      const data = await res.json();
      setTasks([...tasks, data]);
      setNewTaskTitle('');
      setIsModalOpen(false);
    } catch {
      setNewTaskTitle('');
      setIsModalOpen(false);
    }
  };

  // Filtering
  const myDayTasks = tasks.filter(t => t.category === 'my_day');
  const upcomingTasks = tasks.filter(t => t.category === 'upcoming');
  const columns = {
    'Backlog': tasks.filter(t => t.status === 'Backlog'),
    'In Progress': tasks.filter(t => t.status === 'In Progress'),
    'In Review': tasks.filter(t => t.status === 'In Review')
  };

  return (
    <div style={styles.appContainer}>
      {/* SIDEBAR / NAV */}
      <nav style={styles.nav}>
        <div style={styles.logoRow}>
          <div style={styles.logoIcon}>F</div>
          <h1 style={styles.logoText}>Flow</h1>
        </div>
        <div style={styles.menu}>
          <NavButton active={view === 'my_day'} onClick={() => setView('my_day')} icon={<ListTodo size={18}/>} label="My Day" />
          <NavButton active={view === 'kanban'} onClick={() => setView('kanban')} icon={<KanbanSquare size={18}/>} label="Command Centre" />
          <NavButton active={view === 'dashboard'} onClick={() => setView('dashboard')} icon={<LayoutDashboard size={18}/>} label="Leadership" />
        </div>
      </nav>

      {/* MAIN CONTENT AREA */}
      <main style={styles.main}>
        
        {/* VIEW 1: MY DAY */}
        {view === 'my_day' && (
          <div style={styles.pageFade}>
            <header style={styles.header}>
              <h2 style={styles.pageTitle}>Good Morning, Debashis</h2>
              <p style={styles.subTitle}>Here is your focus for today.</p>
            </header>

            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <span style={{...styles.badge, background: '#fee2e2', color: '#dc2626'}}>Priority Tasks</span>
              </div>
              <div style={styles.cardList}>
                {myDayTasks.map(task => (
                  <div key={task.id} style={styles.taskRow}>
                    <div style={styles.taskLeft}>
                      <CheckCircle2 size={20} color="#cbd5e1" style={{marginRight: 15}}/>
                      <div>
                        <div style={styles.taskText}>{task.title}</div>
                        <div style={styles.projectTag}>{task.project}</div>
                      </div>
                    </div>
                    <span style={styles.metaText}>{task.meta}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{...styles.section, marginTop: 40}}>
              <div style={styles.sectionHeader}>
                <span style={{...styles.badge, background: '#f1f5f9', color: '#475569'}}>Upcoming</span>
              </div>
              <div style={styles.cardList}>
                {upcomingTasks.map(task => (
                  <div key={task.id} style={styles.taskRow}>
                    <div style={styles.taskLeft}>
                      <Clock size={20} color="#cbd5e1" style={{marginRight: 15}}/>
                      <div>
                        <div style={styles.taskText}>{task.title}</div>
                        <div style={styles.projectTag}>{task.project}</div>
                      </div>
                    </div>
                    <span style={styles.metaText}>{task.meta}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* VIEW 2: KANBAN */}
        {view === 'kanban' && (
          <div style={styles.pageFade}>
             <header style={styles.header}>
              <h2 style={styles.pageTitle}>Project Command Centre</h2>
            </header>
            <DragDropContext onDragEnd={onDragEnd}>
              <div style={styles.board}>
                {Object.entries(columns).map(([colId, colTasks]) => (
                  <Droppable key={colId} droppableId={colId}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} style={styles.column}>
                        <div style={styles.colHeader}>{colId} <span style={styles.countBadge}>{colTasks.length}</span></div>
                        {colTasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...styles.kanbanCard,
                                  ...provided.draggableProps.style,
                                  boxShadow: snapshot.isDragging ? '0 10px 20px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)'
                                }}
                              >
                                <div style={styles.cardTitle}>{task.title}</div>
                                <div style={styles.cardMeta}>{task.project}</div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                ))}
              </div>
            </DragDropContext>
          </div>
        )}

        {/* VIEW 3: LEADERSHIP DASHBOARD */}
        {view === 'dashboard' && (
           <div style={styles.pageFade}>
            <header style={styles.header}>
              <h2 style={styles.pageTitle}>Strategic Dashboard</h2>
              <p style={styles.subTitle}>Real-time insight into team velocity and health.</p>
            </header>
            
            <div style={styles.chartCard}>
              <h3 style={styles.chartTitle}>Staff Utilisation (Weekly Hours)</h3>
              <div style={{height: 300, width: '100%'}}>
                <ResponsiveContainer>
                  <BarChart data={stats}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} />
                    <YAxis axisLine={false} tickLine={false} />
                    <Tooltip cursor={{fill: '#f8fafc'}} />
                    <Bar dataKey="hours" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
           </div>
        )}
      </main>

      {/* FAB - CREATE TASK */}
      <button style={styles.fab} onClick={() => setIsModalOpen(true)}><Plus size={24} /></button>

      {/* SIMPLE MODAL */}
      {isModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Add New Task</h3>
            <form onSubmit={handleCreateTask}>
              <input 
                autoFocus
                placeholder="What needs to be done?" 
                style={styles.input} 
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
              />
              <div style={styles.modalActions}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={styles.btnSec}>Cancel</button>
                <button type="submit" style={styles.btnPri}>Add Task</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- SUB-COMPONENT ---
const NavButton = ({ active, onClick, icon, label }) => (
  <button style={active ? styles.navBtnActive : styles.navBtn} onClick={onClick}>
    {icon} <span style={{marginLeft: 10}}>{label}</span>
  </button>
);

// --- MODERN STYLES ---
const styles = {
  appContainer: { display: 'flex', minHeight: '100vh', fontFamily: '"Inter", "Segoe UI", sans-serif', backgroundColor: '#f8fafc', color: '#1e293b' },
  nav: { width: '260px', backgroundColor: '#ffffff', borderRight: '1px solid #e2e8f0', padding: '24px', display: 'flex', flexDirection: 'column' },
  logoRow: { display: 'flex', alignItems: 'center', marginBottom: '40px' },
  logoIcon: { width: 32, height: 32, background: '#4f46e5', color: '#fff', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: 12 },
  logoText: { fontSize: '1.25rem', fontWeight: '700', letterSpacing: '-0.5px' },
  menu: { display: 'flex', flexDirection: 'column', gap: '8px' },
  navBtn: { display: 'flex', alignItems: 'center', padding: '12px', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 500, transition: 'all 0.2s' },
  navBtnActive: { display: 'flex', alignItems: 'center', padding: '12px', border: 'none', background: '#eef2ff', color: '#4f46e5', cursor: 'pointer', borderRadius: '8px', fontSize: '0.95rem', fontWeight: 600 },
  
  main: { flex: 1, padding: '40px', overflowY: 'auto' },
  pageFade: { animation: 'fadeIn 0.3s ease-in' },
  header: { marginBottom: '32px' },
  pageTitle: { fontSize: '1.875rem', fontWeight: '700', marginBottom: '8px' },
  subTitle: { color: '#64748b', fontSize: '1.1rem' },

  // Cards & Lists
  section: { marginBottom: 32 },
  badge: { padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 600, display: 'inline-block', marginBottom: 16 },
  cardList: { background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' },
  taskRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #f1f5f9' },
  taskLeft: { display: 'flex', alignItems: 'center' },
  taskText: { fontWeight: 500, fontSize: '1rem' },
  projectTag: { fontSize: '0.8rem', color: '#94a3b8', marginTop: 2 },
  metaText: { fontSize: '0.85rem', color: '#64748b' },

  // Kanban
  board: { display: 'flex', gap: '24px', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 20 },
  column: { minWidth: '300px', background: '#f1f5f9', padding: '16px', borderRadius: '12px' },
  colHeader: { fontWeight: 600, marginBottom: '16px', color: '#475569', display: 'flex', justifyContent: 'space-between' },
  countBadge: { background: '#cbd5e1', padding: '2px 8px', borderRadius: 12, fontSize: '0.8rem' },
  kanbanCard: { background: '#fff', padding: '16px', borderRadius: '8px', marginBottom: '12px', userSelect: 'none', border: '1px solid #e2e8f0' },
  cardTitle: { fontWeight: 500, marginBottom: 8 },
  cardMeta: { fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' },

  // Chart
  chartCard: { background: '#fff', padding: '32px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0' },
  chartTitle: { fontSize: '1.1rem', fontWeight: 600, marginBottom: 24 },

  // FAB & Modal
  fab: { position: 'fixed', bottom: 40, right: 40, width: 64, height: 64, borderRadius: '50%', background: '#4f46e5', color: '#fff', border: 'none', boxShadow: '0 10px 15px -3px rgba(79, 70, 229, 0.4)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modal: { background: '#fff', padding: 32, borderRadius: 16, width: 400, boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)' },
  input: { width: '100%', padding: '12px', marginTop: 16, marginBottom: 24, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '1rem', outlineColor: '#4f46e5' },
  modalActions: { display: 'flex', gap: 12, justifyContent: 'flex-end' },
  btnPri: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  btnSec: { padding: '10px 20px', background: 'transparent', color: '#64748b', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }
};

export default App;